import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import AIStagingModal from '../../components/feature/AIStagingModal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface UploadedImage {
  id: string;
  name: string;
  url: string;
  roomType?: string;
  file_size: number;
  file_type: string;
}

interface FileWithProgress {
  file: File;
  progress: number;
  id: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
}

export default function DashboardPage() {
  const [isAIStagingOpen, setIsAIStagingOpen] = useState(false);
  const [preselectedStyle, setPreselectedStyle] = useState<string>('modern');
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState<FileWithProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const maxFiles = 20;

  // Load saved images from Supabase on component mount
  useEffect(() => {
    checkAuthAndLoadImages();
  }, []);

  const checkAuthAndLoadImages = async () => {
    try {
      setError(null);
      
      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Auth error:', authError);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      if (!user) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      setIsAuthenticated(true);
      await loadSavedImages(user.id);
      
    } catch (error) {
      console.error('Error checking auth:', error);
      setError('Failed to load dashboard. Please refresh the page.');
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  const loadSavedImages = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading images:', error);
        // Don't throw error, just log it and continue with empty array
        setUploadedImages([]);
        return;
      }

      const images: UploadedImage[] = (data || []).map(img => ({
        id: img.id,
        name: img.file_name,
        url: img.image_url,
        roomType: img.room_type,
        file_size: img.file_size,
        file_type: img.file_type
      }));

      setUploadedImages(images);
    } catch (error) {
      console.error('Error loading saved images:', error);
      setUploadedImages([]);
    } finally {
      setLoading(false);
    }
  };

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const validateFile = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return 'File type not supported. Please use JPG, PNG, or HEIC files.';
    }
    if (file.size > maxFileSize) {
      return 'File size too large. Maximum size is 10MB.';
    }
    return null;
  };

  const detectRoomType = (fileName: string): string => {
    const name = fileName.toLowerCase();
    if (name.includes('living') || name.includes('lounge')) return 'Living Room';
    if (name.includes('kitchen') || name.includes('dining')) return 'Kitchen';
    if (name.includes('bedroom') || name.includes('bed')) return 'Bedroom';
    if (name.includes('bathroom') || name.includes('bath')) return 'Bathroom';
    if (name.includes('office') || name.includes('study')) return 'Office';
    return 'Room';
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isAuthenticated) {
      alert('Please sign in to upload images');
      return;
    }

    const selectedFiles = Array.from(event.target.files || []);
    
    if (selectedFiles.length === 0) return;

    // Validate files and create upload queue
    const validFiles: FileWithProgress[] = [];
    
    for (const file of selectedFiles) {
      if (uploadedImages.length + uploadingFiles.length + validFiles.length >= maxFiles) {
        alert(`Maximum ${maxFiles} files allowed`);
        break;
      }
      
      const error = validateFile(file);
      if (!error) {
        validFiles.push({
          file,
          progress: 0,
          id: generateId(),
          status: 'pending'
        });
      } else {
        alert(`${file.name}: ${error}`);
      }
    }

    if (validFiles.length > 0) {
      setUploadingFiles(prev => [...prev, ...validFiles]);
      
      // Start uploading files
      for (const fileWithProgress of validFiles) {
        await uploadToSupabase(fileWithProgress);
      }
    }

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadToSupabase = async (fileWithProgress: FileWithProgress) => {
    try {
      setUploadingFiles(prev => prev.map(f => 
        f.id === fileWithProgress.id 
          ? { ...f, status: 'uploading' as const }
          : f
      ));

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const file = fileWithProgress.file;
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

      // Upload file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('property-images')
        .upload(fileName, file, {
          onUploadProgress: (progress) => {
            const percentage = (progress.loaded / progress.total) * 100;
            setUploadingFiles(prev => prev.map(f => 
              f.id === fileWithProgress.id 
                ? { ...f, progress: percentage }
                : f
            ));
          }
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('property-images')
        .getPublicUrl(fileName);

      // Save to database
      const { data: dbData, error: dbError } = await supabase
        .from('property_images')
        .insert({
          user_id: user.id,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          image_url: publicUrl,
          room_type: detectRoomType(file.name)
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Update file status to completed
      setUploadingFiles(prev => prev.map(f => 
        f.id === fileWithProgress.id 
          ? { ...f, progress: 100, status: 'completed' as const }
          : f
      ));

      // Add to uploaded images
      const newImage: UploadedImage = {
        id: dbData.id,
        name: dbData.file_name,
        url: dbData.image_url,
        roomType: dbData.room_type,
        file_size: dbData.file_size,
        file_type: dbData.file_type
      };

      setUploadedImages(prev => [newImage, ...prev]);

      // Remove from uploading files after a delay
      setTimeout(() => {
        setUploadingFiles(prev => prev.filter(f => f.id !== fileWithProgress.id));
      }, 2000);

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadingFiles(prev => prev.map(f => 
        f.id === fileWithProgress.id 
          ? { ...f, status: 'error' as const }
          : f
      ));
      alert(`Failed to upload ${fileWithProgress.file.name}: ${error.message || 'Unknown error'}`);
    }
  };

  const uploadedProperties = [
    {
      id: 1,
      address: '123 Oak Street, Beverly Hills, CA',
      images: 5,
      staged: 3,
      status: 'Active',
      price: '$2,450,000'
    },
    {
      id: 2,
      address: '456 Pine Avenue, Malibu, CA',
      images: 8,
      staged: 6,
      status: 'Pending',
      price: '$3,200,000'
    },
    {
      id: 3,
      address: '789 Sunset Blvd, West Hollywood, CA',
      images: 12,
      staged: 8,
      status: 'Active',
      price: '$1,850,000'
    }
  ];

  const recentActivity = [
    { action: 'Property staged', property: '123 Oak Street', time: '2 hours ago' },
    { action: 'Images uploaded', property: '456 Pine Avenue', time: '4 hours ago' },
    { action: 'AI staging completed', property: '789 Sunset Blvd', time: '6 hours ago' },
    { action: 'New property added', property: '321 Maple Drive', time: '1 day ago' }
  ];

  const removeUploadedImage = async (imageId: string) => {
    if (!isAuthenticated) {
      alert('Please sign in to remove images');
      return;
    }

    try {
      // Remove from Supabase
      const { error } = await supabase
        .from('property_images')
        .delete()
        .eq('id', imageId);

      if (error) {
        console.error('Error deleting image:', error);
        alert('Failed to remove image. Please try again.');
        return;
      }

      // Remove from local state
      setUploadedImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error removing image:', error);
      alert('Failed to remove image. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleChooseFiles = () => {
    if (!isAuthenticated) {
      alert('Please sign in to upload images');
      return;
    }
    fileInputRef.current?.click();
  };

  // Add drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      alert('Please sign in to upload images');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Create a fake event to reuse existing file handling logic
      const fakeEvent = {
        target: {
          files: files
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleFileSelect(fakeEvent);
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-red-600 text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => window.location.reload()}>
              <i className="ri-refresh-line mr-2"></i>
              Refresh Page
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show sign in prompt if not authenticated
  if (!loading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-line text-blue-600 text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Sign In Required</h2>
            <p className="text-gray-600 mb-6">Please sign in to access your property dashboard and upload images.</p>
            <div className="space-y-3">
              <Link to="/auth/login">
                <Button className="w-full">
                  <i className="ri-login-box-line mr-2"></i>
                  Sign In
                </Button>
              </Link>
              <Button variant="outline" onClick={() => window.location.reload()}>
                <i className="ri-refresh-line mr-2"></i>
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Dashboard Header */}
      <section className="py-12 bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Property Dashboard</h1>
              <p className="text-gray-600">Upload, manage, and enhance your property listings with AI staging</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={handleChooseFiles}
                  className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center space-x-3"
                >
                  <i className="ri-upload-2-line text-blue-600"></i>
                  <span>Upload Images</span>
                </button>
                <button 
                  onClick={() => setIsAIStagingOpen(true)}
                  className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center space-x-3"
                >
                  <i className="ri-magic-line text-blue-600"></i>
                  <span>AI Staging</span>
                </button>
                <button className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center space-x-3">
                  <i className="ri-edit-line text-blue-600"></i>
                  <span>Edit Property</span>
                </button>
                <button className="w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors flex items-center space-x-3">
                  <i className="ri-share-line text-blue-600"></i>
                  <span>Share Listing</span>
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Statistics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Uploaded Images</span>
                  <span className="font-bold text-blue-600">{uploadedImages.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">AI Staged</span>
                  <span className="font-bold text-green-600">18</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Active Listings</span>
                  <span className="font-bold text-orange-600">12</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Sold This Month</span>
                  <span className="font-bold text-purple-600">6</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Upload Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Upload Property Images</h2>
              
              <div 
                className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-upload-cloud-2-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Drop your images here</h3>
                <p className="text-gray-600 mb-4">or click to browse from your computer</p>
                <Button 
                  variant="outline"
                  onClick={handleChooseFiles}
                >
                  <i className="ri-folder-open-line mr-2"></i>
                  Choose Files
                </Button>
                <p className="text-sm text-gray-500 mt-4">Supports: JPG, PNG, HEIC (Max 10MB each)</p>
              </div>

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.heic"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Show Uploading Files */}
              {uploadingFiles.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Uploading Files ({uploadingFiles.length})
                  </h4>
                  <div className="space-y-3">
                    {uploadingFiles.map((fileWithProgress) => (
                      <div
                        key={fileWithProgress.id}
                        className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-image-line text-blue-600 text-xl"></i>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {fileWithProgress.file.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(fileWithProgress.file.size)}
                          </p>
                          
                          {fileWithProgress.status === 'uploading' && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>Uploading...</span>
                                <span>{Math.round(fileWithProgress.progress)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${fileWithProgress.progress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}

                          {fileWithProgress.status === 'completed' && (
                            <p className="text-green-600 text-sm mt-1">Upload completed</p>
                          )}

                          {fileWithProgress.status === 'error' && (
                            <p className="text-red-600 text-sm mt-1">Upload failed</p>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          {fileWithProgress.status === 'completed' && (
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <i className="ri-check-line text-green-600"></i>
                            </div>
                          )}
                          
                          {fileWithProgress.status === 'uploading' && (
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <i className="ri-loader-4-line text-blue-600 animate-spin"></i>
                            </div>
                          )}

                          {fileWithProgress.status === 'error' && (
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                              <i className="ri-close-line text-red-600"></i>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Display Uploaded Images */}
              {loading ? (
                <div className="mt-6 text-center">
                  <div className="inline-flex items-center space-x-2">
                    <i className="ri-loader-4-line animate-spin text-blue-600"></i>
                    <span className="text-gray-600">Loading your images...</span>
                  </div>
                </div>
              ) : uploadedImages.length > 0 ? (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">
                      Uploaded Images ({uploadedImages.length})
                    </h4>
                    <Button 
                      variant="outline"
                      onClick={() => setIsAIStagingOpen(true)}
                      className="text-sm"
                    >
                      <i className="ri-magic-line mr-2"></i>
                      Stage These Images
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {uploadedImages.map((image) => (
                      <div key={image.id} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                          <img 
                            src={image.url} 
                            alt={image.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => removeUploadedImage(image.id)}
                            className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-600 truncate">{image.roomType}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(image.file_size)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <i className="ri-image-line text-gray-400 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-600">Living Room</p>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <i className="ri-image-line text-gray-400 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-600">Kitchen</p>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <i className="ri-image-line text-gray-400 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-600">Bedroom</p>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <i className="ri-add-line text-gray-400 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-600">Add More</p>
                  </div>
                </div>
              )}
            </div>

            {/* AI Staging Options */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">AI Staging Options</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div 
                  className="border border-gray-200 rounded-xl p-6 hover:border-blue-400 transition-colors cursor-pointer"
                  onClick={() => {
                    setPreselectedStyle('modern');
                    setIsAIStagingOpen(true);
                  }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-home-4-line text-blue-600 text-xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Modern Style</h3>
                  <p className="text-gray-600 text-sm">Clean lines, minimalist furniture, neutral colors</p>
                </div>
                
                <div 
                  className="border border-gray-200 rounded-xl p-6 hover:border-blue-400 transition-colors cursor-pointer"
                  onClick={() => {
                    setPreselectedStyle('traditional');
                    setIsAIStagingOpen(true);
                  }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-plant-line text-blue-600 text-xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Traditional</h3>
                  <p className="text-gray-600 text-sm">Classic furniture, warm colors, cozy atmosphere</p>
                </div>
                
                <div 
                  className="border border-gray-200 rounded-xl p-6 hover:border-blue-400 transition-colors cursor-pointer"
                  onClick={() => {
                    setPreselectedStyle('luxury');
                    setIsAIStagingOpen(true);
                  }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-palette-line text-blue-600 text-xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Luxury</h3>
                  <p className="text-gray-600 text-sm">High-end furniture, premium materials, elegant design</p>
                </div>
              </div>
              
              <div className="mt-6 flex space-x-4">
                <Button onClick={() => setIsAIStagingOpen(true)}>
                  <i className="ri-magic-line mr-2"></i>
                  Start AI Staging
                </Button>
                <Button variant="outline">
                  <i className="ri-eye-line mr-2"></i>
                  Preview Styles
                </Button>
              </div>
            </div>

            {/* Properties List */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Your Properties</h2>
                <div className="flex space-x-2">
                  <button className="p-2 rounded-lg hover:bg-gray-100">
                    <i className="ri-grid-line text-gray-600"></i>
                  </button>
                  <button className="p-2 rounded-lg hover:bg-gray-100">
                    <i className="ri-list-unordered text-gray-600"></i>
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Property</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Images</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">AI Staged</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Price</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedProperties.map((property) => (
                      <tr key={property.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div className="font-medium text-gray-900">{property.address}</div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                            {property.images} photos
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">
                            {property.staged} staged
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded-full text-sm ${
                            property.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {property.status}
                          </span>
                        </td>
                        <td className="py-4 px-4 font-semibold text-gray-900">{property.price}</td>
                        <td className="py-4 px-4">
                          <div className="flex space-x-2">
                            <button className="p-2 rounded-lg hover:bg-blue-100 text-blue-600">
                              <i className="ri-eye-line"></i>
                            </button>
                            <button className="p-2 rounded-lg hover:bg-blue-100 text-blue-600">
                              <i className="ri-edit-line"></i>
                            </button>
                            <button className="p-2 rounded-lg hover:bg-red-100 text-red-600">
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Recent Activity</h2>
            <Link 
              to="/services"
              className="inline-flex items-center justify-center font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-6 py-3 text-base rounded-lg"
            >
              <i className="ri-service-line mr-2"></i>
              View Services
            </Link>
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-blue-600"></i>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{activity.action}</p>
                  <p className="text-gray-600">{activity.property}</p>
                </div>
                <span className="text-gray-500 text-sm">{activity.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Staging Modal */}
      <AIStagingModal 
        isOpen={isAIStagingOpen}
        onClose={() => setIsAIStagingOpen(false)}
        uploadedImages={uploadedImages}
        preselectedStyle={preselectedStyle}
      />

      <Footer />
    </div>
  );
}
